import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import StatusBadge, { ocrStatusVariant, docStatusVariant } from "../components/StatusBadge";
import DocumentPreviewModal from "../components/DocumentPreviewModal";
import { Search as SearchIcon, Eye } from "lucide-react";

export default function SearchPage() {
  const [preview, setPreview] = useState(null);
  const [filters, setFilters] = useState({
    q: "", team_id: "", folder_id: "", year: "", month: "",
    document_type: "", ocr_status: "", upload_source: "",
    date_from: "", date_to: "",
  });
  const [teams, setTeams] = useState([]);
  const [folders, setFolders] = useState([]);
  const [types, setTypes] = useState([]);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [t, f, dt] = await Promise.all([
        api.get("/teams"), api.get("/folders"), api.get("/document-types"),
      ]);
      setTeams(t.data); setFolders(f.data); setTypes(dt.data);
    })();
  }, []);

  const run = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (params.year) params.year = parseInt(params.year);
    if (params.month) params.month = parseInt(params.month);
    try {
      const { data } = await api.get("/search/documents", { params });
      setResults(data);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Search">
      <form onSubmit={run} className="ds-card p-4 mb-4" data-testid="search-form">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-3">
            <label className="ds-label">Full-text query</label>
            <div className="relative">
              <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A93]" />
              <input
                className="ds-input pl-9"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="invoice, employment, contract…"
                data-testid="search-query"
              />
            </div>
          </div>
          <div>
            <label className="ds-label">Team</label>
            <select className="ds-input" value={filters.team_id} onChange={(e) => setFilters({ ...filters, team_id: e.target.value, folder_id: "" })} data-testid="search-team">
              <option value="">Any</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="ds-label">Folder</label>
            <select className="ds-input" value={filters.folder_id} onChange={(e) => setFilters({ ...filters, folder_id: e.target.value })} data-testid="search-folder">
              <option value="">Any</option>
              {folders.filter((f) => !filters.team_id || f.team_id === filters.team_id).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="ds-label">Document type</label>
            <select className="ds-input" value={filters.document_type} onChange={(e) => setFilters({ ...filters, document_type: e.target.value })} data-testid="search-type">
              <option value="">Any</option>
              {types.map((t) => <option key={t.code} value={t.name}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="ds-label">OCR status</label>
            <select className="ds-input" value={filters.ocr_status} onChange={(e) => setFilters({ ...filters, ocr_status: e.target.value })} data-testid="search-ocr">
              <option value="">Any</option>
              <option value="pending">pending</option>
              <option value="processing">processing</option>
              <option value="completed">completed</option>
              <option value="failed">failed</option>
              <option value="needs_manual_classification">needs_manual_classification</option>
            </select>
          </div>
          <div>
            <label className="ds-label">Source</label>
            <select className="ds-input" value={filters.upload_source} onChange={(e) => setFilters({ ...filters, upload_source: e.target.value })} data-testid="search-source">
              <option value="">Any</option>
              <option value="manual">manual</option>
              <option value="email">email</option>
            </select>
          </div>
          <div>
            <label className="ds-label">Year</label>
            <input className="ds-input" type="number" value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })} data-testid="search-year" />
          </div>
          <div>
            <label className="ds-label">Month</label>
            <input className="ds-input" type="number" min="1" max="12" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })} data-testid="search-month" />
          </div>
          <div>
            <label className="ds-label">From</label>
            <input className="ds-input" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="search-from" />
          </div>
          <div>
            <label className="ds-label">To</label>
            <input className="ds-input" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="search-to" />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button className="ds-btn-primary" disabled={busy} type="submit" data-testid="run-search-button">
            {busy ? "Searching…" : "Run search"}
          </button>
          <button type="button" className="ds-btn-secondary" onClick={() => { setFilters({ q: "", team_id: "", folder_id: "", year: "", month: "", document_type: "", ocr_status: "", upload_source: "", date_from: "", date_to: "" }); setResults([]); }}>
            Reset
          </button>
        </div>
      </form>

      <div className="ds-card overflow-x-auto">
        <table className="ds-table" data-testid="search-results-table">
          <thead>
            <tr>
              <th>Filename</th><th>Type</th><th>Folder</th><th>OCR</th><th>Status</th><th>Source</th><th>Date</th><th />
            </tr>
          </thead>
          <tbody>
            {results.map((d) => (
              <tr
                key={d.id}
                onClick={() => setPreview(d)}
                className="cursor-pointer"
                data-testid={`search-row-${d.id}`}
              >
                <td className="font-mono text-[12px] truncate max-w-[280px]">{d.filename}</td>
                <td>{d.document_type || "—"}</td>
                <td className="font-mono text-[11px] text-[#4A4A52]">{d.folder_id.slice(0, 8)}</td>
                <td><StatusBadge variant={ocrStatusVariant(d.ocr_status)}>{d.ocr_status}</StatusBadge></td>
                <td><StatusBadge variant={docStatusVariant(d.status)}>{d.status}</StatusBadge></td>
                <td>{d.upload_source}</td>
                <td className="font-mono text-[11px]">{new Date(d.upload_date).toLocaleString()}</td>
                <td className="text-right">
                  <button
                    className="ds-btn-ghost flex items-center gap-1"
                    onClick={(e) => { e.stopPropagation(); setPreview(d); }}
                    data-testid={`search-preview-${d.id}`}
                    aria-label="preview"
                  >
                    <Eye size={13} /> Preview
                  </button>
                </td>
              </tr>
            ))}
            {results.length === 0 && (
              <tr><td colSpan={8} className="text-center text-[#8A8A93] py-10">No results.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {preview && <DocumentPreviewModal document={preview} onClose={() => setPreview(null)} />}
    </AppShell>
  );
}
